if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          pagination: this.querySelector('.product-media-pagination'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.variantChangeUnsubscriber = undefined;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        
        // Setup thumbnails if they exist
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        
        // if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Initialize pagination dots - works on all screen sizes
        this.initPaginationDots();
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.updatePaginationDots(event.detail.currentElement);
      }
      
      initPaginationDots() {
        if (!this.elements.pagination) return;
        
        // Clear existing bullets
        this.elements.pagination.innerHTML = '';
        
        // Get all slides - on desktop, all slides might be visible in the DOM
        // Filter to only include slides that are actually rendered
        const allSlides = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        
        // On desktop, slides might be in a grid layout, so we check if they exist in DOM
        // On mobile, we check visibility. For desktop, we want all slides that are in the gallery
        const visibleSlides = allSlides.filter(slide => {
          // Check if slide exists in DOM and is not explicitly hidden
          const exists = slide && slide.dataset.mediaId;
          const notHidden = !slide.classList.contains('hidden') && 
                           slide.style.display !== 'none' &&
                           slide.style.visibility !== 'hidden';
          
          // On desktop, if offsetParent is null, it might still be visible in a grid
          // So we check both conditions
          const isVisible = this.mql.matches 
            ? (exists && notHidden) // Desktop: just check if exists and not hidden
            : (slide.offsetParent !== null && exists && notHidden); // Mobile: check offsetParent
          
          return isVisible;
        });
        
        // Only create pagination if there's more than 1 visible slide
        if (visibleSlides.length <= 1) {
          this.elements.pagination.style.display = 'none';
          return;
        }
        
        // Show pagination container - always visible on all screens
        this.elements.pagination.style.display = '';
        this.elements.pagination.style.visibility = 'visible';
        this.elements.pagination.style.opacity = '1';
        
        // Create Swiper pagination bullets for all visible slides
        visibleSlides.forEach((slide, index) => {
          const bullet = document.createElement('span');
          bullet.className = 'swiper-pagination-bullet';
          bullet.setAttribute('aria-label', `Go to slide ${index + 1} of ${visibleSlides.length}`);
          bullet.setAttribute('data-slide-index', index);
          bullet.setAttribute('data-media-id', slide.dataset.mediaId);
          bullet.addEventListener('click', () => {
            const mediaId = slide.dataset.mediaId;
            this.setActiveMedia(mediaId, false);
          });
          this.elements.pagination.appendChild(bullet);
        });
        
        // Add swiper-pagination-bullets class for styling
        this.elements.pagination.classList.add('swiper-pagination-bullets');
        
        // Set initial active bullet
        const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active') || 
                           visibleSlides[0];
        if (activeSlide) {
          this.updatePaginationDots(activeSlide);
        }
        
        // Listen for variant changes to refresh pagination
        this.setupVariantChangeListener();
      }
      
      setupVariantChangeListener() {
        // Listen for variant changes using the theme's pub/sub system
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            () => {
              // Refresh pagination when variant changes (after a short delay for DOM updates)
              setTimeout(() => {
                this.initPaginationDots();
              }, 200);
            }
          );
        }
        
        // Also listen for media gallery updates
        this.elements.viewer.addEventListener('slideChanged', () => {
          // Refresh pagination if slide visibility changed
          const visibleSlides = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'))
            .filter(slide => slide.offsetParent !== null && 
                            !slide.classList.contains('hidden') && 
                            slide.style.display !== 'none');
          const currentBullets = this.elements.pagination.querySelectorAll('.swiper-pagination-bullet');
          
          // If slide count changed, refresh pagination
          if (visibleSlides.length !== currentBullets.length) {
            this.initPaginationDots();
          }
        });
      }
      
      disconnectedCallback() {
        // Clean up variant change listener
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }
      
      updatePaginationDots(activeSlide) {
        if (!this.elements.pagination || !activeSlide) return;
        
        // Get visible slides (same filter as in initPaginationDots)
        const allSlides = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const visibleSlides = allSlides.filter(slide => {
          const exists = slide && slide.dataset.mediaId;
          const notHidden = !slide.classList.contains('hidden') && 
                           slide.style.display !== 'none' &&
                           slide.style.visibility !== 'hidden';
          
          const isVisible = this.mql.matches 
            ? (exists && notHidden)
            : (slide.offsetParent !== null && exists && notHidden);
          
          return isVisible;
        });
        
        const activeIndex = visibleSlides.indexOf(activeSlide);
        
        if (activeIndex === -1) return;
        
        const bullets = this.elements.pagination.querySelectorAll('.swiper-pagination-bullet');
        bullets.forEach((bullet, index) => {
          if (index === activeIndex) {
            bullet.classList.add('swiper-pagination-bullet-active');
          } else {
            bullet.classList.remove('swiper-pagination-bullet-active');
          }
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updatePaginationDots(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
